import { Stack, styled, useTheme } from '@mui/material'
import {
  Circle,
  GoogleMap,
  GoogleMapProps,
  Marker,
  OverlayView,
  useJsApiLoader,
} from '@react-google-maps/api'
import get from 'lodash/get'
import { FC, useRef } from 'react'
import { IconButton, Icons } from 'utoniq-ui'

import { GpsData } from '@/clients/utoniq-core/schema'
import { GpsClientInfo, Location } from '@/components/organisms/gps'
import { config } from '@/config'

type GpsMapData = Pick<
  GpsData,
  'radius' | 'approximateAddress' | 'latitude' | 'longitude'
>

const TOKYO_LOCATION = {
  lat: 35.652832,
  lng: 139.839478,
}

/**
 * @description
 * Map drawing component using google-map
 */
export type GpsMapProps = GoogleMapProps & {
  targetData?: GpsMapData
  currentData?: GpsClientInfo['currentLocation']
  pinData?: Pick<GpsData, 'latitude' | 'longitude'>
  offset?: number
  actions?: {}[]
}

export const GpsMap: FC<GpsMapProps> = ({
  targetData,
  currentData,
  pinData,
  mapContainerStyle = { height: '100%', width: '100%' },
  offset,
  ...props
}) => {
  const libraries = useRef<
    Array<'places' | 'drawing' | 'geometry' | 'localContext' | 'visualization'>
  >(['places'])
  const mapInstanceRef = useRef<google.maps.Map>()
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: config.googleMapApiKey,
    libraries: libraries.current,
  })

  if (!isLoaded) {
    return null
  }

  return (
    <Stack
      flex={1}
      sx={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <GoogleMapStyled
        mapContainerStyle={mapContainerStyle}
        options={{
          fullscreenControl: false,
          mapTypeControl: false,
          zoomControl: false,
          streetViewControl: false,
        }}
        zoom={14}
        {...props}
        onLoad={mapInstance => {
          /**
           * @hack
           * ズーム初期値の設定
           * zoom propsだけでは正しく初期化されないため、読み込み開始後に少し経ってからに設定する
           */
          setTimeout(() => {
            mapInstance?.setZoom(14)
          }, 150)

          // default move to tokyo
          mapInstance.panTo(
            new google.maps.LatLng(TOKYO_LOCATION.lat, TOKYO_LOCATION.lng)
          )
          const bounds = new google.maps.LatLngBounds()
          ;[targetData, currentData].forEach(data => {
            if (data?.latitude && data?.longitude) {
              const latLng = new google.maps.LatLng({
                lat: data.latitude,
                lng: data.longitude,
              })
              bounds.extend(latLng)
              const radius = get(data, 'radius')
              if (radius) {
                const centerCircle = new google.maps.Circle({
                  center: latLng,
                  radius,
                })
                if (centerCircle.getBounds()) {
                  bounds.union(
                    centerCircle.getBounds() as google.maps.LatLngBounds
                  )
                }
              }
            }
          })
          if (
            [targetData, currentData].some(
              data => data?.latitude && data?.longitude
            )
          ) {
            mapInstance.fitBounds(bounds)
          }
          mapInstanceRef.current = mapInstance
          props.onLoad && props.onLoad(mapInstance)
        }}
      >
        {targetData?.latitude && targetData.longitude && (
          <Target {...targetData} />
        )}
        {currentData?.latitude && currentData.longitude && (
          <Current {...currentData} />
        )}
        {pinData?.latitude && pinData.longitude && <Pin {...pinData} />}
      </GoogleMapStyled>
      <Stack
        spacing={3}
        sx={{ position: 'absolute', right: 12, bottom: 12 + (offset ?? 0) }}
      >
        {targetData?.latitude && targetData?.longitude && (
          <IconButton.Spot
            size="medium"
            bgcolor="grey.50"
            color="grey.600"
            onClick={() => {
              mapInstanceRef.current?.panTo({
                lat: targetData.latitude ?? 0,
                lng: targetData.longitude ?? 0,
              })
            }}
          />
        )}
        {currentData?.latitude && currentData?.longitude && (
          <IconButton.Direction
            size="medium"
            bgcolor="grey.50"
            color="grey.600"
            onClick={() => {
              mapInstanceRef.current?.panTo({
                lat: currentData.latitude ?? 0,
                lng: currentData.longitude ?? 0,
              })
            }}
          />
        )}
      </Stack>
    </Stack>
  )
}

type TargetProps = Pick<GpsData, 'radius' | 'latitude' | 'longitude'>
const Target: FC<TargetProps> = ({ latitude, longitude, radius }) => {
  const { palette } = useTheme()

  const defaultCircleOptions = {
    strokeColor: palette.info.dark,
    strokeOpacity: 1,
    strokeWeight: 1,
    fillColor: palette.info.light,
    fillOpacity: 0.5,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    zIndex: 1,
  }

  return (
    <>
      <Marker
        icon={{
          path: 'M20 2C12.26 2 6 8.26 6 16c0 10.5 14 22 14 22s14-11.5 14-22c0-7.74-6.26-14-14-14zm0 19c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z',
          scale: 0.7,
          fillColor: palette.primary.dark,
          fillOpacity: 1,
          strokeWeight: 0,
          anchor: new google.maps.Point(20, 40),
        }}
        zIndex={2}
        clickable={false}
        position={{ lat: latitude, lng: longitude }}
      />
      <Circle
        center={{ lat: latitude, lng: longitude }}
        radius={radius ?? 100}
        options={{ ...defaultCircleOptions, radius }}
      />
    </>
  )
}

const Current: FC<Partial<Location>> = ({ latitude, longitude }) => {
  if (!latitude || !longitude) return null

  return (
    <OverlayView
      mapPaneName="markerLayer"
      position={{ lat: latitude, lng: longitude }}
      getPixelPositionOffset={() => ({ x: -12, y: -12 })}
    >
      <Icons.CurrentLocation />
    </OverlayView>
  )
}

const Pin: FC<Partial<Location>> = ({ latitude, longitude }) => {
  const { palette } = useTheme()

  if (!latitude || !longitude) return null

  return (
    <Marker
      icon={{
        path: 'M20 2C12.26 2 6 8.26 6 16c0 10.5 14 22 14 22s14-11.5 14-22c0-7.74-6.26-14-14-14zm0 19c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z',
        scale: 0.7,
        fillColor: palette.primary.main,
        fillOpacity: 1,
        strokeWeight: 0,
        anchor: new google.maps.Point(20, 40),
      }}
      position={{ lat: latitude, lng: longitude }}
    />
  )
}

const GoogleMapStyled = styled(
  ({ className, ...props }: GoogleMapProps & { className?: string }) => (
    <GoogleMap {...props} mapContainerClassName={className} />
  )
)({
  '.gm-style .gm-style-iw-a': {
    display: 'none',
  },
})
